import { useCallback, useMemo, useRef, useState } from 'react'

import { HERO_VIEW } from '../../shared/constants/heroView.js'
import { SECTION_ID } from '../../shared/constants/sectionIds.js'

export function useAppNavigationState({ scrollToSectionWhenReady, initialHeroView = HERO_VIEW.VISUALIZATION }) {
  const [activeProduct, setActiveProduct] = useState(null)
  const [activeBuilding, setActiveBuilding] = useState(null)
  const [activeTier, setActiveTier] = useState(null)
  const [activeSectionId, setActiveSectionId] = useState(SECTION_ID.HERO)
  const [activeHeroView, setActiveHeroView] = useState(initialHeroView)
  const prevInitialHeroViewRef = useRef(initialHeroView)

  // During-render sync: when initialHeroView changes (config/capability update),
  // immediately follow the new value if the user hasn't explicitly navigated away.
  // Using the React "adjusting state based on a prop" pattern instead of useEffect
  // to prevent the 1-frame flash when HeroView first mounts after config loads.
  if (prevInitialHeroViewRef.current !== initialHeroView) {
    const wasAtPrevDefault = activeHeroView === prevInitialHeroViewRef.current
    prevInitialHeroViewRef.current = initialHeroView
    if (wasAtPrevDefault) {
      setActiveHeroView(initialHeroView)
    }
  }

  const scrollToTarget = useCallback((scrollTarget, scrollOptions) => {
    if (!scrollTarget) return
    scrollToSectionWhenReady(scrollTarget, 10, scrollOptions)
  }, [scrollToSectionWhenReady])

  const activateSectionHandler = useCallback((sectionId, { scrollTarget, scrollOptions } = {}) => {
    setActiveSectionId(sectionId)
    scrollToTarget(scrollTarget, scrollOptions)
  }, [scrollToTarget])

  const replaceNavigationStateHandler = useCallback(({
    sectionId = SECTION_ID.HERO,
    productId = null,
    buildingId = null,
    tierId = null,
    heroView,
    scrollTarget = null,
    scrollOptions,
  } = {}) => {
    setActiveSectionId(sectionId)
    setActiveProduct(productId)
    setActiveBuilding(buildingId)
    setActiveTier(tierId)

    if (heroView) {
      setActiveHeroView(heroView)
    }

    scrollToTarget(scrollTarget, scrollOptions)
  }, [scrollToTarget])

  const changeHeroViewHandler = useCallback((heroView) => {
    if (!heroView) return
    setActiveHeroView(heroView)
  }, [])

  const clearProductSelectionHandler = useCallback(({ sectionId, scrollTarget, scrollOptions } = {}) => {
    setActiveProduct(null)
    setActiveBuilding(null)
    setActiveTier(null)

    if (sectionId) {
      setActiveSectionId(sectionId)
    }

    scrollToTarget(scrollTarget, scrollOptions)
  }, [scrollToTarget])

  const selectProductHandler = useCallback((uuid, {
    buildingId,
    refTier,
    scrollTarget = SECTION_ID.PRODUCT,
    scrollOptions,
  } = {}) => {
    if (!uuid) {
      clearProductSelectionHandler()
      return
    }

    replaceNavigationStateHandler({
      sectionId: SECTION_ID.PRODUCT,
      productId: uuid,
      buildingId: buildingId ?? null,
      tierId: refTier ?? null,
      scrollTarget,
      scrollOptions,
    })
  }, [clearProductSelectionHandler, replaceNavigationStateHandler])

  const navigationActions = useMemo(() => ({
    activateSectionHandler,
    replaceNavigationStateHandler,
    clearProductSelectionHandler,
    selectProductHandler,
    changeHeroViewHandler,
  }), [
    activateSectionHandler,
    replaceNavigationStateHandler,
    clearProductSelectionHandler,
    selectProductHandler,
    changeHeroViewHandler,
  ])

  return {
    activeProduct,
    activeBuilding,
    activeTier,
    activeSectionId,
    activeHeroView,
    setActiveProduct,
    setActiveBuilding,
    setActiveTier,
    setActiveSectionId,
    setActiveHeroView,
    activateSectionHandler,
    replaceNavigationStateHandler,
    clearProductSelectionHandler,
    selectProductHandler,
    changeHeroViewHandler,
    navigationActions,
  }
}